import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Cloud, Loader2, CheckCircle, FolderOpen, FileSpreadsheet, X } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { DataSourceSelector } from '../../data-sources/DataSourceSelector';
import { dataSourceService } from '../../../services/dataSourceService';
import { DataSource, BrowseResult } from '../../../types/dataSource';

const formatSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const CloudTransferNode: React.FC<{ data: { label: string; color: string; isTemplate?: boolean } }> = ({ data }) => {
  const [selectedSource, setSelectedSource] = useState<DataSource | null>(null);
  const [browseData, setBrowseData] = useState<BrowseResult | null>(null);
  const [browsing, setBrowsing] = useState(false);
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [transferring, setTransferring] = useState(false);
  const [transferred, setTransferred] = useState<number>(0);
  const [error, setError] = useState('');

  const handleSourceSelect = async (source: DataSource | null) => {
    setSelectedSource(source);
    setBrowseData(null);
    setSelectedFiles([]);
    setTransferred(0);
    setError('');
    if (source) {
      setBrowsing(true);
      try {
        const data = await dataSourceService.browseSource(source.source_id);
        setBrowseData(data);
        setCurrentPrefix(data.prefix);
      } catch (e: any) {
        setError(e.message || 'Browse failed');
      } finally {
        setBrowsing(false);
      }
    }
  };

  const navigateFolder = async (prefix: string) => {
    if (!selectedSource) return;
    setBrowsing(true);
    try {
      const data = await dataSourceService.browseSource(selectedSource.source_id, prefix);
      setBrowseData(data);
      setCurrentPrefix(prefix);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBrowsing(false);
    }
  };

  const toggleFile = (key: string) => {
    setSelectedFiles((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };

  const handleTransfer = async () => {
    if (!selectedSource || selectedFiles.length === 0) return;
    setTransferring(true);
    setError('');
    try {
      const res = await dataSourceService.transferFiles(selectedSource.source_id, selectedFiles);
      setTransferred(res.count);
      setSelectedFiles([]);
    } catch (e: any) {
      setError(e.message || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="bg-card border rounded-xl shadow-md w-[340px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <Cloud className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3 space-y-2">
        <DataSourceSelector
          typeFilter={['s3', 'gcp']}
          selectedSourceId={selectedSource?.source_id || null}
          onSourceSelect={handleSourceSelect}
          compact
          onAddSource={() => window.open('/upload', '_blank')}
        />

        {browsing && (
          <div className="flex items-center gap-2 py-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-xs">Browsing...</span>
          </div>
        )}

        {browseData && !browsing && (
          <div className="space-y-1.5">
            {currentPrefix && (
              <button
                className="text-[11px] text-primary hover:underline"
                onClick={() => {
                  const parent = currentPrefix.replace(/[^/]+\/$/, '');
                  navigateFolder(parent);
                }}
              >
                .. (up)
              </button>
            )}

            <div className="max-h-[140px] overflow-auto space-y-0.5 nodrag nowheel">
              {browseData.folders.map((f) => (
                <button
                  key={f}
                  className="flex items-center gap-1.5 text-[11px] w-full text-left hover:bg-muted/50 rounded px-1.5 py-0.5"
                  onClick={() => navigateFolder(f)}
                >
                  <FolderOpen className="h-3 w-3 text-amber-500 shrink-0" />
                  <span className="truncate">{f.replace(currentPrefix, '')}</span>
                </button>
              ))}
              {browseData.files.map((f) => (
                <label
                  key={f.key}
                  className="flex items-center gap-1.5 text-[11px] w-full hover:bg-muted/50 rounded px-1.5 py-0.5 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="h-3 w-3"
                    checked={selectedFiles.includes(f.key)}
                    onChange={() => toggleFile(f.key)}
                  />
                  <FileSpreadsheet className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="truncate flex-1">{f.key.split('/').pop()}</span>
                  <span className="text-muted-foreground text-[10px]">{formatSize(f.size)}</span>
                </label>
              ))}
              {browseData.folders.length === 0 && browseData.files.length === 0 && (
                <p className="text-[11px] text-muted-foreground text-center py-2">Empty</p>
              )}
            </div>

            {selectedFiles.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                className="w-full text-xs h-7"
                onClick={handleTransfer}
                disabled={transferring}
              >
                {transferring ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : null}
                Import {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}
              </Button>
            )}
          </div>
        )}

        {transferred > 0 && (
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
            <span className="text-xs">{transferred} file{transferred > 1 ? 's' : ''} imported</span>
            <Button variant="ghost" size="icon" className="h-6 w-6 ml-auto" onClick={() => setTransferred(0)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        {error && (
          <p className="text-xs text-destructive">{error}</p>
        )}
      </div>

      <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
    </div>
  );
};
